import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import bgImage from '../assets/bg.jpg';
import logo from '../assets/logo.svg';
import toast from 'react-hot-toast';
import { msalInstance, initializeMsal } from '../utils/msalInstance';
import MicrosoftLogo from '../assets/Microsoft_logo.svg';
import Loader from '../components/loader';
import serverURL from '../config';
import { useAuth } from '../context/AuthContext';

const getMicrosoftButtonContent = (loadingMicrosoft, msalReady) => {
  if (loadingMicrosoft) return 'Logging in...';
  if (!msalReady) return 'Loading...';
  return (
    <div className="flex items-center justify-center">
      <div className="bg-white w-auto flex flex-wrap p-1 rounded mr-2">
        <img src={MicrosoftLogo} className="inline-block w-5 h-6 md:w-6 md:h-6" alt="Microsoft logo" />
      </div>
      LOGIN WITH MICROSOFT
    </div>
  );
};

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const { login: authLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingMicrosoft, setLoadingMicrosoft] = useState(false);
  const [msalReady, setMsalReady] = useState(false);
  const redirectProcessedRef = useRef(false);
  const isMountedRef = useRef(true);

  const finalizeMicrosoftLogin = useCallback(async (accessToken) => {
    if (!accessToken) {
      throw new Error('Unable to validate the Microsoft login response.');
    }

    const backendResponse = await axios.post(
      `${serverURL}/auth/login/microsoft`,
      { accessToken },
      { timeout: 10000, withCredentials: true }
    );
    const { user } = backendResponse.data;
    toast.success('Successfully logged in with Microsoft!');
    authLogin(user);
  }, [authLogin]);

  const handleRedirectResponse = useCallback(async (response) => {
    if (!isMountedRef.current) return;

    setLoading(true);
    setLoadingMicrosoft(true);
    try {
      let accessToken = response?.accessToken;

      if (!accessToken && response?.account) {
        msalInstance.setActiveAccount(response.account);
        const tokenResult = await msalInstance.acquireTokenSilent({
          account: response.account,
          scopes: ['User.Read'],
        });
        accessToken = tokenResult?.accessToken;
      } else if (response?.account) {
        msalInstance.setActiveAccount(response.account);
      }

      await finalizeMicrosoftLogin(accessToken);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Microsoft login failed. Please try again.';
      toast.error(errorMsg, { duration: 5000 });
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setLoadingMicrosoft(false);
      }
    }
  }, [finalizeMicrosoftLogin]);

  useEffect(() => {
    isMountedRef.current = true;

    const initAndHandleRedirect = async () => {
      try {
        await initializeMsal();

        if (!redirectProcessedRef.current) {
          redirectProcessedRef.current = true;

          const redirectResponse = await msalInstance.handleRedirectPromise();

          if (redirectResponse !== null) {
            await handleRedirectResponse(redirectResponse);
            return;
          }

          // Fallback: redirect response is null (e.g. React StrictMode double-render),
          // but a cached account exists — attempt silent token renewal.
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            const account = accounts[0];
            msalInstance.setActiveAccount(account);
            try {
              const tokenRes = await msalInstance.acquireTokenSilent({
                account,
                scopes: ['User.Read'],
              });
              // Only proceed if we actually received a valid access token
              if (tokenRes?.accessToken) {
                await finalizeMicrosoftLogin(tokenRes.accessToken);
              } else {
                console.warn('MSAL silent token returned no accessToken — skipping auto-login.');
              }
            } catch (silentErr) {
              // Silent acquisition failed (session expired / user not in cache).
              // Do NOT call finalizeMicrosoftLogin — the user must log in manually.
              console.warn('MSAL silent token acquisition failed — user must re-authenticate:', silentErr?.message || silentErr);
            }
            return;
          }
        }
      } catch (err) {
        console.error('MSAL initialization failed:', err);
      } finally {
        if (isMountedRef.current) {
          setMsalReady(true);
          setLoading(false);
          setLoadingMicrosoft(false);
        }
      }
    };

    initAndHandleRedirect();

    return () => {
      isMountedRef.current = false;
    };
  }, [finalizeMicrosoftLogin, handleRedirectResponse]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${serverURL}/auth/login`,
        formData,
        { withCredentials: true }
      );
      const { user } = response.data;
      toast.success('Successfully logged in!');
      authLogin(user);
    } catch (err) {
      console.error('Login error:', err);
      const errorMsg = err.response?.data?.message || 'Login failed. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    if (!msalReady) {
      toast.error('Authentication service is still loading. Please wait...');
      return;
    }
    if (loading || loadingMicrosoft) return;

    setLoading(true);
    setLoadingMicrosoft(true);
    try {
      await msalInstance.loginRedirect({
        scopes: ['User.Read'],
        prompt: 'select_account',
      });
      // Execution stops here — page redirects
    } catch (err) {
      console.error('Microsoft login redirect error:', err);
      if (isMountedRef.current) {
        setLoadingMicrosoft(false);
        setLoading(false);
        const errorMsg = err.errorMessage || 'Microsoft login failed. Please try again.';
        toast.error(errorMsg);
      }
    }
  };

  return (
    <div>
      {loading && <Loader />}
      <div className="relative min-h-screen overflow-y-auto">
        <div
          className="fixed inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${bgImage})` }}
        />
        <div className="relative min-h-screen flex items-center justify-center py-8 px-4">
          <div className="bg-campus-surface p-4 px-8 md:p-8 rounded-2xl shadow-hover w-full max-w-md">
            <div className="flex justify-center mb-6">
              <img src={logo} alt="vThink Logo" className="h-10 md:h-20 w-auto" />
            </div>
            <h2 className="md:text-xl font-heading font-semibold mb-3 text-center text-campus-text-primary">Login</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email*"
                  autoComplete="email"
                  required
                  className="w-full p-2 md:p-3 rounded-lg bg-campus-bg border border-campus-border text-campus-text-primary placeholder-campus-text-body focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition"
                />
              </div>
              <div>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Password*"
                  autoComplete="current-password"
                  required
                  className="w-full p-2 md:p-3 rounded-lg bg-campus-bg border border-campus-border text-campus-text-primary placeholder-campus-text-body focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition"
                />
              </div>
              {error && <div className="text-campus-error text-sm text-center">{error}</div>}
              <button
                type="submit"
                className="text-sm md:text-lg w-full text-white p-2 md:p-3 rounded-lg bg-campus-text-primary hover:bg-campus-text-body transition-colors duration-300 font-medium"
              >
                LOGIN
              </button>
            </form>

            <div className="flex items-center my-2 md:my-3">
              <hr className="flex-grow border-campus-border" />
              <span className="text-sm md:text-lg mx-2 text-campus-text-body">OR</span>
              <hr className="flex-grow border-campus-border" />
            </div>

            <button
              type="button"
              onClick={handleMicrosoftLogin}
              className={`text-sm md:text-lg w-full text-white p-2 md:p-3 rounded-lg transition-colors duration-300 font-medium ${loadingMicrosoft || !msalReady ? 'bg-campus-text-body cursor-not-allowed' : 'bg-campus-primary hover:bg-campus-primary-dark'}`}
              disabled={loadingMicrosoft || !msalReady}
            >
              {getMicrosoftButtonContent(loadingMicrosoft, msalReady)}
            </button>

            <p className="text-center text-campus-text-body mt-3 text-sm">
              Don&apos;t have an account yet?{' '}
              <a href="/register" className="text-campus-primary hover:text-campus-primary-dark font-medium">
                Sign Up
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
